// Shared Recharts theme tokens for the dark/light Telegram-style UI.
// Static data colors (green, red, etc.) are theme-independent.
// Layout colors (grid, axis, tooltip) adapt via CSS custom properties.

import { useSyncExternalStore } from "react";

// ── Static data colors (same in both themes) ───────────────────────────────

export const CHART_COLORS = {
	green: "#4ade80",
	red: "#f87171",
	blue: "#60a5fa",
	yellow: "#fbbf24",
	purple: "#a78bfa",
	slate: "#94a3b8",
} as const;

export const CHART_FONT = {
	family:
		'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
	size: 11,
} as const;

// ── Theme-aware layout colors ───────────────────────────────────────────────
// These are resolved from CSS custom properties so they adapt to light/dark.
// For HTML elements (tooltips), use the CSS_VARS object with var() strings.
// For Recharts SVG props, use the useChartTheme() hook which resolves values.

/** CSS variable strings — safe to use in style={{}} on HTML elements */
export const CSS_VARS = {
	tooltip: "var(--color-tg-card)",
	tooltipBorder: "var(--color-tg-line-strong)",
	cardBg: "var(--color-tg-surface)",
} as const;

// Subscribe to theme changes by watching the data-theme attribute on <html>
const subscribe = (callback: () => void): (() => void) => {
	const observer = new MutationObserver((mutations) => {
		for (const m of mutations) {
			if (m.attributeName === "data-theme") {
				callback();
				return;
			}
		}
	});
	observer.observe(document.documentElement, { attributes: true });
	return () => observer.disconnect();
};

const resolveColors = () => {
	const style = getComputedStyle(document.documentElement);
	const get = (name: string, fallback: string) =>
		style.getPropertyValue(name).trim() || fallback;
	const isLight =
		document.documentElement.dataset.theme === "light";
	return {
		grid: get("--color-tg-line", "#1e293b"),
		axis: get("--color-tg-muted", "#64748b"),
		muted: get("--color-tg-muted", "#475569"),
		pieLabelFill: isLight ? "#1c2f42" : "#ffffff",
	};
};

let cachedSnapshot = resolveColors();
subscribe(() => {
	cachedSnapshot = resolveColors();
});

/** Hook that returns theme-aware colors for Recharts SVG props. */
export const useChartTheme = () =>
	useSyncExternalStore(subscribe, () => cachedSnapshot);
